import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { H2, H3 } from './Typography';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  actions?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    loading?: boolean;
    disabled?: boolean;
  }[];
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
}

const sizeClasses = {
  sm: 'max-w-[calc(100vw-2rem)] sm:max-w-md',
  md: 'max-w-[calc(100vw-2rem)] sm:max-w-lg',
  lg: 'max-w-[calc(100vw-2rem)] sm:max-w-2xl',
  xl: 'max-w-[calc(100vw-2rem)] sm:max-w-4xl',
  full: 'max-w-full mx-2 sm:mx-4 my-2 sm:my-4 h-[calc(100vh-1rem)] sm:h-[calc(100vh-2rem)]',
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  actions = [],
  className = '',
  overlayClassName = '',
  contentClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Focus management
  useEffect(() => {
    if (isOpen) {
      // Use a timeout to ensure the modal is rendered before focusing
      const timeout = setTimeout(() => {
        // Try to focus the close button first
        const closeButton = modalRef.current?.querySelector('button[aria-label="Close modal"]') as HTMLElement;
        if (closeButton) {
          closeButton.focus();
        } else if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
      
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  const overlayVariants = {
    closed: { opacity: 0 },
    open: { opacity: 1 },
  };

  const modalVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      y: 20,
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${overlayClassName}`}
          initial="closed"
          animate="open"
          exit="closed"
          variants={overlayVariants}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-gray-900 bg-opacity-50 backdrop-blur-sm" />

          {/* Modal */}
          <motion.div
            ref={modalRef}
            className={`relative w-full ${sizeClasses[size]} ${className}`}
            variants={modalVariants}
            transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? 'modal-title' : undefined}
          >
            <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 ${size === 'full' ? 'h-full flex flex-col' : ''} ${contentClassName}`}>
              {/* Header */}
              {(title || showCloseButton) && (
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                  {title && (
                    <H2 id="modal-title" className="text-gray-900 dark:text-gray-100 text-lg sm:text-xl">
                      {title}
                    </H2>
                  )}
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Close modal"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  )}
                </div>
              )}

              {/* Content */}
              <div className={`p-4 sm:p-6 ${size === 'full' ? 'flex-1 overflow-y-auto' : 'max-h-[60vh] overflow-y-auto'}`}>
                {children}
              </div>

              {/* Actions */}
              {actions.length > 0 && (
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-2xl">
                  {actions.map((action, index) => (
                    <Button
                      key={index}
                      variant={action.variant || 'secondary'}
                      onClick={action.onClick}
                      isLoading={action.loading}
                      disabled={action.disabled}
                      fullWidth={undefined}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { Modal };
export default Modal;
